import axios from 'axios'
import UserService from './user.service'

const LOCAL_HOSTS = ['localhost', '127.0.0.1']
const DEFAULT_BACKEND_PORT = '8000'

const ensureTrailingSlash = (url) => (url.endsWith('/') ? url : `${url}/`)

const getWindowLocation = () => {
    if (typeof window === 'undefined' || !window.location) {
        return { hostname: 'localhost', protocol: 'http:' }
    }
    const { hostname, protocol } = window.location
    return {
        hostname: hostname || 'localhost',
        protocol: protocol || 'http:'
    }
}

const buildDevBaseURL = () => {
    const { hostname, protocol } = getWindowLocation()
    const targetHost = LOCAL_HOSTS.includes(hostname) ? 'localhost' : hostname
    return ensureTrailingSlash(`${protocol}//${targetHost}:${DEFAULT_BACKEND_PORT}/`)
}

const resolveBaseURL = () => {
    const envUrl = process.env.VUE_APP_ROOT_API
    const isServe = process.env.VUE_APP_NODE_NAME === 'SERVE'

    if (isServe) {
        return buildDevBaseURL()
    }

    if (envUrl && envUrl.length > 0) {
        return ensureTrailingSlash(envUrl)
    }

    const { hostname, protocol } = getWindowLocation()
    return ensureTrailingSlash(`${protocol}//${hostname}/`)
}

function onGetCall(config) {
    return config.method === 'get';
  }

const ApiService = {

   // Stores the 401 interceptor position so that it can be later ejected when needed
   //_401interceptor: null,

    get baseURL() {
        return resolveBaseURL()
    },
    get isLocal() {
        return process.env.VUE_APP_NODE_NAME === 'SERVE'
    },

    async init() {
        axios.defaults.withCredentials = true
        
        // Laravel Sanctum kompatibilis CSRF kezelés
        axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN'
        axios.defaults.xsrfCookieName = 'XSRF-TOKEN'
        
        // Request interceptor CSRF token kezeléshez
        axios.interceptors.request.use(
            (config) => {
                // Ha már van XSRF token cookie-ban, használjuk azt
                const token = this.getCookie('XSRF-TOKEN')
                if (token) {
                    config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token)
                }
                
                // Laravel számára szükséges header-ök
                config.headers['Accept'] = 'application/json'
                config.headers['X-Requested-With'] = 'XMLHttpRequest'
                
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
        
        this.mount401Interceptor()
        this.mountGetHeaderInterceptor()
    },

    // Cookie olvasó helper
    getCookie(name) {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) return parts.pop().split(';').shift()
        return null
    },

    get(resource, data = "") {
        if(data === ""){
            return axios.get(this.baseURL + resource)
        }else{
            return axios.get(this.baseURL + resource, { params: data })
        }
    },

    post(resource, data) {
        return axios.post(this.baseURL + resource, data)       
    },

    put(resource, data) {
        return axios.put(this.baseURL + resource, data)
    },

    delete(resource) {
        return axios.delete(this.baseURL + resource)
    },

    /**
     * Perform a custom Axios request.
     *
     * data is an object containing the following properties:
     *  - method
     *  - url
     *  - data ... request payload
     *  - auth (optional)
     *    - username
     *    - password
    **/
   // baseURL not defined here! 
    customRequest(data) {
        if(!this.isLocal){
            data.url = this.baseURL + data.url
            data.url = data.url.replace('//','/')
        }
        return axios(data)
    },

    // Amikor lejár a session, de benn vagyunk az app-ban, akkor a felhasználót át kell irányítani a login oldalra
    mount401Interceptor() {
        this._401interceptor = axios.interceptors.response.use(
            (response) => {
                return response
            },
            async (error) => {
                if (error.request.status == 401) {
                    const url = error.request.responseURL
                    const URLlastPart = url.substr(url.lastIndexOf("/")+1)
                    if(URLlastPart !== 'user'){         // csak akkor kell logout, ha nem /api/user -t hívtunk
                        await UserService.logout(true) // true => is401 miatti logout, nem lesz logout api hívás
                    }else{
                        throw error
                    }                } else if (error.request.status == 419) {
                    // CSRF token mismatch - próbáljuk újra egy új token-nel
                    //console.warn('CSRF token mismatch (419) - új token kérése...')
                    
                    try {
                        // Új CSRF cookie lekérése
                        await axios.get(this.baseURL + 'sanctum/csrf-cookie', {
                            withCredentials: true
                        })
                        
                        // Kis várakozás
                        await new Promise(resolve => setTimeout(resolve, 100))
                        
                        // Eredeti kérés újrapróbálása
                        const token = this.getCookie('XSRF-TOKEN')
                        if (token) {
                            error.config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token)
                        }
                        
                        //console.log('Kérés újrapróbálása új CSRF token-nel...')
                        return axios.request(error.config)
                        
                    } catch (retryError) {
                        //console.error('CSRF token újrapróbálás sikertelen:', retryError)
                        throw error
                    }
                }else{
                    // If error was not 401 just reject as is
                    throw error
                }
            }
        )
    },

    unmount401Interceptor() {
        // Eject the interceptor
        axios.interceptors.response.eject(this._401interceptor)
    },

    mountGetHeaderInterceptor() {
        axios.interceptors.response.use(function (response) {
            if(response.headers['x-frontend-version'] !== undefined && process.env.VUE_APP_VERSION !== undefined
               && response.headers['x-frontend-version'] > process.env.VUE_APP_VERSION)
                location.reload(true);
            return response
          }, null, { runWhen: onGetCall });
    }

}

export default ApiService
